import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { UserPlus, ShieldAlert, Users, Send, UserX, UserCheck } from 'lucide-react';
import { pb, currentUser } from '~/lib/pb';
import { useTenantContext } from '~/lib/tenantContext';
import type { User } from '~/lib/types';
import { PasswordInput } from '~/components/PasswordInput/PasswordInput';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { formatPbError } from '~/lib/pb-error';
import { can, ROLE_LABELS, ROLE_DESCRIPTIONS } from '~/lib/permissions';
import styles from './_authed.team.module.css';

// Equipo vive en Personas. /settings?tab=team redirige acá.
export const Route = createFileRoute('/_authed/team')({
  beforeLoad: () => {
    if (!can(currentUser()?.role, 'manageTeam')) {
      throw redirect({ to: '/settings' });
    }
  },
  component: TeamPage,
});

function TeamPage() {
  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Equipo</h1>
          <p className="page-header__subtitle">
            Personas con acceso a este cliente. Puedes invitar administradores y vendedores. Si
            alguien ya no debe entrar, desactiva su cuenta: no se borra.
          </p>
        </div>
      </header>
      <TeamView />
    </div>
  );
}

type Role = 'client_admin' | 'sales_rep';

/**
 * Vista de equipo embebida en Settings. Sin wrapper de página ni h1 —
 * el wrapper lo provee el tab panel de Settings.
 */
export function TeamView() {
  const me = currentUser();
  const { selectedTenant } = useTenantContext();
  const [open, setOpen] = useState(false);

  // client_admin gestiona el equipo de SU tenant; superadmin el de cualquiera.
  // sales_rep no gestiona equipo.
  if (!can(me?.role, 'manageTeam')) {
    return (
      <div className={styles.notSuperadmin}>
        <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
        <p>
          La gestión de equipo está disponible solo para administradores.
          <br />
          Si necesitas agregar a alguien, pídeselo al administrador de tu cuenta.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.embedded}>
      <div className={styles.embeddedHead}>
        <p className={styles.subtitle}>El correo de invitación les deja crear su clave.</p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setOpen(true)}
          disabled={!selectedTenant}
        >
          <UserPlus size={16} aria-hidden />
          Invitar usuario
        </button>
      </div>

      {selectedTenant && me?.role === 'superadmin' ? (
        <div className={styles.tenantBar}>
          <span className={styles.tenantBar__label}>Viendo equipo de:</span>
          <span className={styles.tenantBar__name}>{selectedTenant.name}</span>
        </div>
      ) : !selectedTenant ? (
        <div className={styles.empty}>
          Selecciona un cliente en el switcher del topbar para ver su equipo.
        </div>
      ) : null}

      {selectedTenant && <TeamTable tenantId={selectedTenant.id} />}

      {open && selectedTenant && (
        <InviteUserModal
          tenantId={selectedTenant.id}
          tenantName={selectedTenant.name}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function TeamTable({ tenantId }: { tenantId: string }) {
  const me = currentUser();
  const qc = useQueryClient();
  const confirm = useConfirm();
  // Feedback por fila (reenvío ok / error de borrado). Se limpia al cambiar.
  const [rowMsg, setRowMsg] = useState<{ id: string; text: string; tone: 'ok' | 'err' } | null>(
    null,
  );

  const usersQuery = useQuery({
    queryKey: ['users', 'by-tenant', tenantId],
    queryFn: async () => {
      const res = await pb.collection('users').getList<User>(1, 100, {
        filter: `tenant = "${tenantId}"`,
        sort: 'name',
      });
      return res.items;
    },
    enabled: !!tenantId,
  });

  const resendMut = useMutation({
    mutationFn: async (u: User) => {
      await pb.collection('users').requestPasswordReset(u.email);
      return u;
    },
    onSuccess: (u) =>
      setRowMsg({
        id: u.id,
        text: 'Invitación reenviada — link nuevo enviado al correo.',
        tone: 'ok',
      }),
    onError: (err, u) =>
      setRowMsg({ id: u.id, text: formatPbError(err, 'No se pudo reenviar.'), tone: 'err' }),
  });

  const activeMut = useMutation({
    mutationFn: async (u: User) => {
      const next = u.active === false;
      await pb.collection('users').update(u.id, { active: next });
      return { user: u, active: next };
    },
    onSuccess: ({ user, active }) => {
      setRowMsg({
        id: user.id,
        text: active
          ? 'Cuenta reactivada. Ya puede entrar.'
          : 'Cuenta desactivada. Ya no puede entrar.',
        tone: 'ok',
      });
      qc.invalidateQueries({ queryKey: ['users', 'by-tenant', tenantId] });
    },
    onError: (err, u) =>
      setRowMsg({
        id: u.id,
        text: formatPbError(err, 'No se pudo cambiar el estado.'),
        tone: 'err',
      }),
  });

  async function handleActive(u: User) {
    const turningOff = u.active !== false;
    const name = u.display_name || u.name || u.email;
    const ok = await confirm({
      title: turningOff ? `Desactivar a ${name}` : `Reactivar a ${name}`,
      description: turningOff ? (
        <>
          Deja de poder entrar. Sus oportunidades y tareas siguen en el cliente. Puedes reactivarlo
          después.
        </>
      ) : (
        <>Vuelve a poder entrar con su correo y su clave.</>
      ),
      confirmLabel: turningOff ? 'Desactivar' : 'Reactivar',
      tone: turningOff ? 'danger' : 'primary',
    });
    if (!ok) return;
    activeMut.mutate(u);
  }

  if (usersQuery.isLoading) return <div className={styles.empty}>Cargando…</div>;

  const users = usersQuery.data ?? [];
  if (users.length === 0) {
    return (
      <div className="empty-state">
        <Users size={28} aria-hidden className="empty-state__icon" />
        <p>
          Aún no hay usuarios en este cliente. Haz clic en «Invitar usuario» para crear el primero.
        </p>
      </div>
    );
  }

  function rowActions(u: User, inactive: boolean, pending: boolean, canToggle: boolean) {
    return (
      <>
        {pending && (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => resendMut.mutate(u)}
            disabled={resendMut.isPending}
            title="Reenviar invitación"
            aria-label="Reenviar invitación"
          >
            <Send size={14} aria-hidden />
          </button>
        )}
        {canToggle && (
          <button
            type="button"
            className={`${styles.textAction} ${inactive ? '' : styles.textActionDanger}`}
            onClick={() => handleActive(u)}
            disabled={activeMut.isPending}
          >
            {inactive ? <UserCheck size={14} aria-hidden /> : <UserX size={14} aria-hidden />}
            {inactive ? 'Reactivar' : 'Desactivar'}
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <div className={styles.cards}>
        {users.map((u) => {
          const inactive = u.active === false;
          const pending = !inactive && !u.last_login;
          const isSelf = me?.id === u.id;
          const roleLabel =
            u.role === 'client_admin' || u.role === 'sales_rep'
              ? ROLE_LABELS[u.role]
              : 'Superadmin';
          const canToggle = !isSelf && u.role !== 'superadmin';
          return (
            <article key={u.id} className={styles.card}>
              <div>
                <strong>{u.display_name || u.name || '—'}</strong>
                <div className={styles.cardEmail}>{u.email}</div>
              </div>
              <div className={styles.cardMeta}>
                <span className={styles.roleTag}>{roleLabel}</span>
                <span
                  className={`${styles.statusChip} ${
                    inactive
                      ? styles.statusChipOff
                      : pending
                        ? styles.statusChipPending
                        : styles.statusChipActive
                  }`}
                >
                  {inactive ? 'Desactivada' : pending ? 'Pendiente' : 'Activa'}
                </span>
              </div>
              {rowMsg?.id === u.id && (
                <div className={rowMsg.tone === 'ok' ? styles.rowMsgOk : styles.rowMsgErr}>
                  {rowMsg.text}
                </div>
              )}
              <div className={styles.cardActions}>
                {rowActions(u, inactive, pending, canToggle)}
              </div>
            </article>
          );
        })}
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Estado</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const inactive = u.active === false;
              const pending = !inactive && !u.last_login;
              const isSelf = me?.id === u.id;
              const roleLabel =
                u.role === 'client_admin' || u.role === 'sales_rep'
                  ? ROLE_LABELS[u.role]
                  : 'Superadmin';
              const canToggle = !isSelf && u.role !== 'superadmin';
              return (
                <tr key={u.id}>
                  <td>{u.display_name || u.name || '—'}</td>
                  <td>
                    {u.email}
                    {rowMsg?.id === u.id && (
                      <div className={rowMsg.tone === 'ok' ? styles.rowMsgOk : styles.rowMsgErr}>
                        {rowMsg.text}
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        u.role === 'superadmin'
                          ? `${styles.roleTag} ${styles.roleTag__superadmin}`
                          : styles.roleTag
                      }
                    >
                      {roleLabel}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusChip} ${
                        inactive
                          ? styles.statusChipOff
                          : pending
                            ? styles.statusChipPending
                            : styles.statusChipActive
                      }`}
                    >
                      {inactive ? 'Desactivada' : pending ? 'Pendiente' : 'Activa'}
                    </span>
                  </td>
                  <td className={styles.actionsCell}>
                    {rowActions(u, inactive, pending, canToggle)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

interface InviteUserModalProps {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
}

function InviteUserModal({ tenantId, tenantName, onClose }: InviteUserModalProps) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('client_admin');
  // Modo de activación: 'invite' = link de reset por email (el usuario pone su
  // clave); 'manual' = tú asignas la clave ahora (sin email), igual que /admins.
  const [mode, setMode] = useState<'invite' | 'manual'>('invite');
  const [password, setPassword] = useState(() => generatePassword());
  const [error, setError] = useState<string | null>(null);
  // Estado post-creación: mensaje de éxito. `manual` distingue el modo; en
  // invite, `resetSent=false` significa que el email falló → fallback.
  const [success, setSuccess] = useState<{
    email: string;
    fallbackPassword: string;
    resetSent: boolean;
    manual: boolean;
  } | null>(null);

  const inviteMut = useMutation({
    mutationFn: async () => {
      const trimmedEmail = email.trim().toLowerCase();
      const manual = mode === 'manual';
      // En manual usamos la clave que escribió el superadmin; en invite, una
      // aleatoria que el usuario reemplaza vía el link de reset.
      const pwd = manual ? password : generatePassword();
      await pb.collection('users').create({
        email: trimmedEmail,
        password: pwd,
        passwordConfirm: pwd,
        name: name.trim() || trimmedEmail.split('@')[0],
        display_name: name.trim() || trimmedEmail.split('@')[0],
        role,
        tenant: tenantId,
        emailVisibility: true,
        verified: true,
      });
      // Solo en modo invite disparamos el email con link de reset. En manual
      // el superadmin le pasa la clave al usuario por su cuenta.
      let resetSent = false;
      if (!manual) {
        try {
          await pb.collection('users').requestPasswordReset(trimmedEmail);
          resetSent = true;
        } catch (err) {
          console.warn('[invite] requestPasswordReset failed', err);
        }
      }
      return { email: trimmedEmail, fallbackPassword: pwd, resetSent, manual };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['users', 'by-tenant', tenantId] });
      setSuccess(data);
    },
    onError: (err) => {
      const msg = formatPbError(err, 'No se pudo crear el usuario.');
      // Email unique: usually a superadmin (or another tenant) already owns it.
      if (/unique|already|exists|valor debe ser único/i.test(msg)) {
        setError(
          'Ese correo ya existe en Focus (p. ej. como superadmin). No se puede duplicar: asígnalo como anfitrión de la agenda, o usa otro email.',
        );
        return;
      }
      setError(msg);
    },
  });

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (!email.includes('@')) {
      setError('El correo no es válido.');
      return;
    }
    if (mode === 'manual' && password.trim().length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    inviteMut.mutate();
  };

  return (
    <div
      className={styles.modalBackdrop}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !inviteMut.isPending) onClose();
      }}
    >
      {success ? (
        <InviteSuccess
          email={success.email}
          resetSent={success.resetSent}
          fallbackPassword={success.fallbackPassword}
          manual={success.manual}
          onClose={onClose}
        />
      ) : (
        <form className={styles.modal} onSubmit={handleSubmit}>
          <header className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Agregar usuario</h2>
            <p className={styles.modalSubtitle}>
              A <strong>{tenantName}</strong>.{' '}
              {mode === 'invite'
                ? 'Le mandamos un email con un link para que cree su contraseña.'
                : 'Le asignas la contraseña ahora y se la pasas tú.'}
            </p>
          </header>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="invite-email">
              Email
            </label>
            <input
              id="invite-email"
              className={styles.fieldInput}
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              required
              autoFocus
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="invite-name">
              Nombre (opcional)
            </label>
            <input
              id="invite-name"
              className={styles.fieldInput}
              type="text"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="ej. María González"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="invite-role">
              Rol
            </label>
            <select
              id="invite-role"
              className={styles.fieldSelect}
              value={role}
              onChange={(ev) => setRole(ev.target.value as Role)}
            >
              <option value="client_admin">{ROLE_LABELS.client_admin}</option>
              <option value="sales_rep">{ROLE_LABELS.sales_rep}</option>
            </select>
            <small
              style={{
                color: 'var(--color-text-subtle)',
                fontSize: 'var(--font-size-meta)',
                lineHeight: 1.4,
                display: 'block',
                marginTop: 4,
              }}
            >
              {ROLE_DESCRIPTIONS[role]}
            </small>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel} id="invite-mode-label">
              Cómo activar la cuenta
            </span>
            <div
              className={styles.segControl}
              role="radiogroup"
              aria-labelledby="invite-mode-label"
            >
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'invite'}
                className={`${styles.segOption} ${mode === 'invite' ? styles.segOptionActive : ''}`}
                onClick={() => setMode('invite')}
              >
                Invitar por email
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'manual'}
                className={`${styles.segOption} ${mode === 'manual' ? styles.segOptionActive : ''}`}
                onClick={() => setMode('manual')}
              >
                Asignar contraseña
              </button>
            </div>
          </div>

          {mode === 'manual' && (
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="invite-pwd">
                Contraseña
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <PasswordInput
                  id="invite-pwd"
                  className={styles.fieldInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonGhost}`}
                  onClick={() => setPassword(generatePassword())}
                  title="Generar contraseña"
                >
                  Generar
                </button>
              </div>
              <small
                style={{ color: 'var(--color-text-subtle)', fontSize: 'var(--font-size-meta)' }}
              >
                Mínimo 8 caracteres. Pásasela al usuario por un canal seguro; puede cambiarla al
                entrar.
              </small>
            </div>
          )}

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonGhost}`}
              onClick={onClose}
              disabled={inviteMut.isPending}
            >
              Cancelar
            </button>
            <button type="submit" className={styles.button} disabled={inviteMut.isPending}>
              {inviteMut.isPending
                ? 'Creando…'
                : mode === 'invite'
                  ? 'Enviar invitación'
                  : 'Crear usuario'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function InviteSuccess({
  email,
  resetSent,
  fallbackPassword,
  manual,
  onClose,
}: {
  email: string;
  resetSent: boolean;
  fallbackPassword: string;
  manual: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // Mostramos la contraseña cuando: el modo fue manual (la asignaste a
  // propósito), o el email de invitación falló (fallback).
  const showPassword = manual || !resetSent;
  return (
    <div className={styles.modal}>
      <header className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>
          {resetSent ? 'Invitación enviada ✓' : 'Usuario creado ✓'}
        </h2>
        <p className={styles.modalSubtitle}>
          {resetSent ? (
            <>
              Le mandamos un email a <strong>{email}</strong> con un link para que cree su propia
              contraseña. El link dura 24 horas; si se le pasa, usa el botón{' '}
              <strong>Reenviar</strong> en la fila de la tabla — no hace falta volver a crearlo.
            </>
          ) : manual ? (
            <>
              Usuario creado en <strong>{email}</strong> con la contraseña que asignaste. Pásasela
              por un canal seguro; puede cambiarla al entrar.
            </>
          ) : (
            <>
              El usuario quedó creado en <strong>{email}</strong>, pero no pudimos enviarle el email
              automático (SMTP). Pásale esta contraseña temporal por un canal seguro y dile que la
              cambie al entrar.
            </>
          )}
        </p>
      </header>

      {showPassword && (
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {manual ? 'Contraseña' : 'Contraseña temporal'}
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className={styles.fieldInput}
              type="text"
              value={fallbackPassword}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              style={{ fontFamily: 'monospace' }}
            />
            <button
              type="button"
              className={`${styles.button} ${styles.buttonGhost}`}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(fallbackPassword);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {
                  /* ignore */
                }
              }}
            >
              {copied ? '✓ Copiada' : 'Copiar'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.modalFooter}>
        <button type="button" className={styles.button} onClick={onClose}>
          Cerrar
        </button>
      </div>
    </div>
  );
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  let out = '';
  const arr = new Uint32Array(14);
  crypto.getRandomValues(arr);
  for (let i = 0; i < arr.length; i++) out += chars[arr[i] % chars.length];
  return out;
}
